import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import ScrollToTop from "@/components/feature/ScrollToTop";
import Breadcrumb from "@/components/feature/Breadcrumb";
import SeoHead from "@/components/feature/SeoHead";
import { categories } from "@/mocks/categories";
import { articles } from "@/mocks/articles";

export default function ArticlePage() {
  const { id } = useParams();
  const article = articles.find((a) => a.id === Number(id));

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  if (!article) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen flex items-center justify-center pt-16">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-primary-100">
              <i className="ri-file-unknow-line text-3xl text-primary-500"></i>
            </div>
            <h2 className="text-xl font-heading font-semibold text-foreground-950 mb-2">
              文章不存在
            </h2>
            <Link
              to="/"
              className="text-primary-500 hover:text-primary-600 text-sm"
            >
              返回首页
            </Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const category = categories.find((c) => c.slug === article.category);
  const relatedArticles = articles
    .filter(
      (a) => a.category === article.category && a.id !== article.id
    )
    .slice(0, 3);

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: article.title,
    description: article.summary,
    image: article.thumbnail,
    author: {
      "@type": "Organization",
      name: article.author,
    },
    datePublished: article.publishDate,
    dateModified: article.publishDate,
    publisher: {
      "@type": "Organization",
      name: "夫妻影院",
    },
  };

  return (
    <>
      <SeoHead
        title={`${article.title}`}
        description={article.summary}
        keywords={`${article.tags.join(",")},夫妻影院,夫妻电影,${category?.name || "电影推荐"}`}
        canonicalUrl={`https://fuqicinema.com/article/${article.id}`}
        ogType="article"
        ogImage={article.thumbnail}
        articleData={{
          publishedTime: article.publishDate,
          modifiedTime: article.publishDate,
          author: article.author,
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
      />
      <Navbar />
      <main className="pt-16">
        <div className="bg-background-50 border-b border-secondary-200">
          <div className="max-w-4xl mx-auto">
            <Breadcrumb
              items={[
                { label: "首页", path: "/" },
                { label: category?.name || "分类", path: `/category/${article.category}` },
                { label: article.title },
              ]}
            />
          </div>
        </div>

        <article className="py-8 md:py-12 bg-background-50">
          <div className="px-4 md:px-6 max-w-4xl mx-auto">
            <div className="mb-8">
              <div className="flex items-center gap-3 mb-4 flex-wrap">
                {category && (
                  <Link
                    to={`/category/${category.slug}`}
                    className="text-xs text-primary-600 bg-primary-100 px-3 py-1 rounded-full whitespace-nowrap hover:bg-primary-200 transition-colors"
                  >
                    {category.name}
                  </Link>
                )}
                <span className="text-xs text-foreground-500">{article.year}</span>
                <span className="text-xs text-foreground-500">{article.duration}</span>
                <span className="text-xs text-foreground-500">{article.readTime}</span>
                <span className="flex items-center gap-1 text-xs text-accent-600">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-star-fill text-xs"></i>
                  </div>
                  {article.rating}
                </span>
              </div>

              <h1 className="text-xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground-950 mb-4 leading-tight">
                {article.title}
              </h1>

              <div className="flex items-center gap-3 text-xs text-foreground-500 mb-6">
                <span>{article.author}</span>
                <span>·</span>
                <span>{article.publishDate}</span>
              </div>

              <div className="flex flex-wrap gap-1.5 mb-6">
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="text-xs text-secondary-700 bg-secondary-100 px-2.5 py-1 rounded-full"
                  >
                    {tag}
                  </span>
                ))}
              </div>

              <div className="relative aspect-video rounded-xl overflow-hidden mb-8">
                <img
                  src={article.thumbnail}
                  alt={article.title}
                  title={`${article.title} - 夫妻影院`}
                  className="w-full h-full object-cover"
                />
              </div>

              <div
                className="prose prose-sm md:prose-base max-w-none text-foreground-800 leading-relaxed"
                style={{
                  fontFamily: "var(--font-body)",
                }}
              >
                <style>{`
                  .prose h2 { font-family: var(--font-heading); color: oklch(var(--foreground-950)); font-size: 1.4rem; margin-top: 2rem; margin-bottom: 0.75rem; }
                  .prose h3 { font-family: var(--font-heading); color: oklch(var(--foreground-900)); font-size: 1.1rem; margin-top: 1.5rem; margin-bottom: 0.5rem; }
                  .prose p { margin-bottom: 1rem; line-height: 1.85; }
                  .prose strong { color: oklch(var(--primary-600)); }
                `}</style>
                <div
                  dangerouslySetInnerHTML={{ __html: article.content }}
                />
              </div>
            </div>
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="py-10 md:py-14 bg-background-100">
            <div className="px-4 md:px-6 max-w-7xl mx-auto">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-6 md:mb-8 text-center">
                相关推荐
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                {relatedArticles.map((rel) => (
                  <Link
                    key={rel.id}
                    to={`/article/${rel.id}`}
                    className="group bg-background-50 rounded-xl overflow-hidden border border-secondary-200 transition-all duration-300 hover:border-primary-300"
                  >
                    <div className="relative aspect-video overflow-hidden">
                      <img
                        src={rel.thumbnail}
                        alt={rel.title}
                        title={rel.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                      />
                      <div className="absolute top-3 left-3 px-2.5 py-1 rounded-full bg-black/60 text-white text-xs">
                        {rel.rating}分
                      </div>
                    </div>
                    <div className="p-4">
                      <h3 className="font-heading text-sm font-semibold text-foreground-950 mb-2 line-clamp-2 group-hover:text-primary-500 transition-colors">
                        {rel.title}
                      </h3>
                      <p className="text-xs text-foreground-600 line-clamp-2">
                        {rel.summary}
                      </p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
      <Footer />
      <ScrollToTop />
    </>
  );
}